import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { HexColorPicker } from "react-colorful";
import { applyAreaConfig, APPT_TEMPLATE_PATTERN, PRIO_ORDER } from "../constants";
import { fetchAreas, fetchCategories, createArea, updateArea, deleteArea, fetchSetting, saveSetting } from "../api";
import { formatISODate } from "../lib/dates";
import PrioIcon from "./PrioIcon";
import FilterBar from "./FilterBar";
import ConfirmDialog from "./ConfirmDialog";
import "./AreaView.css";

function formatDateShort(dateStr) {
  if (!dateStr) return "\u2014";
  const parts = dateStr.split("-");
  if (parts.length !== 3) return dateStr;
  return `${parts[2]}.${parts[1]}.${parts[0].slice(2)}`;
}


function mapAreaFromApi(k) {
  return { key: k.key, label: k.label, abbreviation: k.abbreviation, bg: k.bg, border: k.border_color, text: k.text_color, sort_order: k.sort_order, admin_only: k.admin_only, version: k.version };
}

const SORT_KEYS = ["name", "prio", "area", "person", "deadline", "calendar"];
const DEFAULT_SORT = { by: "name", dir: "asc" };

/* Klickbare Spaltenüberschrift. Der Pfeil zeigt die Richtung der AKTIVEN
   Spalte; bei den übrigen taucht er nur beim Hovern auf, damit die Kopfzeile
   nicht zur Pfeilparade wird. */
function SortHeader({ label, sortKey, sort, onSort, className }) {
  const active = sort.by === sortKey;
  return (
    <th
      className={className}
      aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
    >
      <button
        type="button"
        className={`list-sort-btn ${active ? "is-active" : ""}`}
        onClick={(e) => { e.stopPropagation(); onSort(sortKey); }}
        title={`Nach ${label} sortieren${active ? (sort.dir === "asc" ? " (aufsteigend)" : " (absteigend)") : ""}`}
      >
        {label}
        <svg
          className={`list-sort-icon ${active && sort.dir === "desc" ? "is-desc" : ""}`}
          width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
        >
          <polyline points="6 15 12 9 18 15" />
        </svg>
      </button>
    </th>
  );
}

export default function AreaView({ tasks, allTasks = null, appointments = [], employees, areas: areasProp = [], onEdit, onQuickUpdate, onDuplicate, onDelete, onNewTask, onAddAppointment, onEditAppointment, onDeleteAppointment, onDuplicateAppointment, filters, onFiltersChange, isAdmin }) {
  const [selected, setSelected] = useState("__open__");
  const [showFilters, setShowFilters] = useState(false);
  const [collapsedCats, setCollapsedCats] = useState({});
  const [tagsCollapsed, setTagsCollapsed] = useState(false);
  const [generalCollapsed, setGeneralCollapsed] = useState(false);
  const [areaOrder, setAreaOrder] = useState([]);
  // First-paint gate (same pattern as ProjectsPage/Time): the sidebar order lives in
  // the stored "areaOrder" setting, which arrives AFTER the areas prop is already
  // renderable — painting before it lands makes the list visibly re-sort itself.
  const [areasReady, setAreasReady] = useState(false);
  const [dividerPos, setDividerPos] = useState(() => {
    try {
      // NaN from a corrupt value would make the divider unreachable for good.
      const v = parseInt(localStorage.getItem("areaDividerPos"), 10);
      return Number.isFinite(v) ? v : areasProp.length;
    } catch { return areasProp.length; }
  });
  // Spaltensortierung der Task-Tabelle, überlebt den Reload.
  const [sort, setSort] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem("listSort") || "null");
      if (s && SORT_KEYS.includes(s.by) && (s.dir === "asc" || s.dir === "desc")) return s;
    } catch {}
    return DEFAULT_SORT;
  });
  // Gleiche Spalte -> Richtung umdrehen, andere Spalte -> dort aufsteigend starten.
  const toggleSort = (key) => {
    setSort((s) => {
      const next = s.by === key ? { by: key, dir: s.dir === "asc" ? "desc" : "asc" } : { by: key, dir: "asc" };
      try { localStorage.setItem("listSort", JSON.stringify(next)); } catch {}
      return next;
    });
  };
  const [selectedTasks, setSelectedTasks] = useState(new Set());
  const [selectedAppointments, setSelectedAppointments] = useState(new Set());
  const [confirmBulkDeleteAppointments, setConfirmBulkDeleteAppointments] = useState(false);
  const [showBulkMoveMenu, setShowBulkMoveMenu] = useState(false);
  const [showBulkCatMenu, setShowBulkCatMenu] = useState(false);
  const [bulkCategories, setBulkCategories] = useState([]);
  const [bulkCatInput, setBulkCatInput] = useState("");
  const [areaEditing, setAreaEditing] = useState(false);
  const [areaCreating, setAreaCreating] = useState(false);
  const [areaError, setAreaError] = useState("");
  const [confirmDeleteArea, setConfirmDeleteArea] = useState(false);
  const [editLabel, setEditLabel] = useState("");
  const [editColor, setEditColor] = useState("#3b82f6");
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [colorPickerPos, setColorPickerPos] = useState(null);
  const [editAdminOnly, setEditAdminOnly] = useState(false);
  const [apiAreas, setApiAreas] = useState(null);
  const [specialViewConfig, setSpecialViewConfig] = useState(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem("specialViewConfig") || "{}");
      // A stored "null"/array would crash getSpecialView on mount.
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
    } catch { return {}; }
  });
  const [mobileAreaOpen, setMobileAreaOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" ? window.innerWidth <= 768 : false);
  useEffect(() => {
    const mql = window.matchMedia("(max-width: 768px)");
    const handler = (e) => setIsMobile(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);

  const filterRef = useRef(null);
  const dragArea = useRef(null);
  const bulkMoveMenuRef = useRef(null);
  const bulkCatMenuRef = useRef(null);
  const colorBtnRef = useRef(null);
  const colorPickerRef = useRef(null);

  useEffect(() => {
    if (!showFilters) return;
    function handleClickOutside(e) {
      if (filterRef.current && !filterRef.current.contains(e.target) &&
          !e.target.closest(".custom-select-dropdown")) {
        setShowFilters(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showFilters]);

  useEffect(() => {
    if (!showColorPicker) return;
    function handleClickOutside(e) {
      if (
        colorBtnRef.current && !colorBtnRef.current.contains(e.target) &&
        colorPickerRef.current && !colorPickerRef.current.contains(e.target)
      ) {
        setShowColorPicker(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showColorPicker]);

  useEffect(() => {
    if (!showBulkMoveMenu) return;
    function handleClickOutside(e) {
      if (bulkMoveMenuRef.current && !bulkMoveMenuRef.current.contains(e.target)) {
        setShowBulkMoveMenu(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showBulkMoveMenu]);

  useEffect(() => {
    if (!showBulkCatMenu) return;
    function handleClickOutside(e) {
      if (bulkCatMenuRef.current && !bulkCatMenuRef.current.contains(e.target)) {
        setShowBulkCatMenu(false);
        setBulkCatInput("");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showBulkCatMenu]);

  // Load areas and area order from API — both are awaited before the first paint.
  useEffect(() => {
    Promise.all([
      fetchAreas()
        .then((data) => setApiAreas(data.map(mapAreaFromApi)))
        .catch(() => {}),
      fetchSetting("areaOrder")
        .then((data) => {
          if (!data?.value) return;
          const order = JSON.parse(data.value);
          // A non-array here would crash areaOrder.map/.includes below.
          if (Array.isArray(order)) setAreaOrder(order);
        })
        .catch(() => {}),
    ]).finally(() => setAreasReady(true));
  }, []);

  const reloadAreas = () => {
    fetchAreas()
      .then((data) => {
        setApiAreas(data.map(mapAreaFromApi));
        applyAreaConfig(data);
      })
      .catch(() => {});
  };

  // Clear bulk selections whenever the active area/view OR the filters change —
  // a filter switch can hide selected rows, and bulk delete would otherwise
  // still hit the now-invisible tasks.
  useEffect(() => {
    setSelectedTasks(new Set());
    setSelectedAppointments(new Set());
  }, [selected, filters]);

  const toggleCat = (cat) => {
    setCollapsedCats((prev) => ({ ...prev, [cat]: !prev[cat] }));
  };

  const baseAreas = useMemo(() => apiAreas || areasProp, [apiAreas, areasProp]);

  // Stored order first, anything not covered by it appended. Derived here (not in a
  // follow-up effect) so the list is already in its final order when it first paints.
  const allAreas = useMemo(() => {
    const ordered = areaOrder.map((key) => baseAreas.find((k) => k.key === key)).filter(Boolean);
    const missing = baseAreas.filter((k) => !areaOrder.includes(k.key));
    return [...ordered, ...missing];
  }, [baseAreas, areaOrder]);

  const SPECIAL_DEFAULTS = {
    __open__: { label: "Offene Tasks", color: "#3870d8" },
    __appointments__: { label: "Alle Termine", color: "#caa45e" },
  };

  const getSpecialView = (key) => {
    const def = SPECIAL_DEFAULTS[key];
    const cfg = specialViewConfig[key] || {};
    return { label: cfg.label || def.label, color: cfg.color || def.color };
  };

  const isOpenSelected = selected === "__open__";
  const isAppointmentsSelected = selected === "__appointments__";
  const isSpecialView = isOpenSelected || isAppointmentsSelected;

  // Load categories when bulk cat menu opens
  useEffect(() => {
    if (!showBulkCatMenu) return;
    const area = !isSpecialView ? selected : null;
    fetchCategories(area).then(setBulkCategories).catch(() => {});
  }, [showBulkCatMenu, selected, isSpecialView]);

  // Open tasks = tasks without calendar_date
  const openTasks = useMemo(() => tasks.filter((t) => !t.calendar_date), [tasks]);

  const filteredTasks = useMemo(() => (
    isOpenSelected ? openTasks
      : isAppointmentsSelected ? []
      : tasks.filter((t) => t.area === selected)
  ), [tasks, openTasks, isOpenSelected, isAppointmentsSelected, selected]);

  // Sortierschlüssel = das, was in der Spalte STEHT (Bereich/Person als Text,
  // Prio nach ihrer Rangfolge statt alphabetisch).
  const areaLabelOf = useMemo(() => {
    const m = {};
    for (const a of allAreas) m[a.key] = a.label;
    return m;
  }, [allAreas]);

  const sortValue = useCallback((t, key) => {
    switch (key) {
      case "prio": return PRIO_ORDER[t.prio] ?? 99;
      case "area": return (areaLabelOf[t.area] || t.area || "").toLowerCase();
      case "person": return (t.persons || []).map((p) => p.name).join(", ").toLowerCase();
      case "deadline": return t.deadline || "";
      case "calendar": return t.calendar_date || "";
      default: return (t.name || "").toLowerCase();
    }
  }, [areaLabelOf]);

  const compareTasks = useCallback((a, b) => {
    const va = sortValue(a, sort.by);
    const vb = sortValue(b, sort.by);
    // Leere Zellen (kein Datum, keine Person) IMMER ans Ende — auch absteigend;
    // ein Block leerer Zeilen oben ist beim Sortieren nie das Gewünschte.
    const ea = va === "" || va === 99;
    const eb = vb === "" || vb === 99;
    if (ea !== eb) return ea ? 1 : -1;
    const c = typeof va === "number" ? va - vb : String(va).localeCompare(String(vb), "de");
    if (c !== 0) return sort.dir === "desc" ? -c : c;
    // Stabiler Tiebreak, damit gleiche Werte nicht bei jedem Render springen.
    return (a.name || "").localeCompare(b.name || "", "de")
      || (a.calendar_date || "").localeCompare(b.calendar_date || "");
  }, [sort, sortValue]);

  // Grouping + per-category sorting used to re-run on EVERY render (every
  // checkbox click, every keystroke in the category input) — memoized.
  const { grouped, sortedCats } = useMemo(() => {
    const g = {};
    if (!isSpecialView) {
      for (const t of filteredTasks) {
        const cat = t.category || "keine";
        if (!g[cat]) g[cat] = [];
        g[cat].push(t);
      }
      // Sortiert wird INNERHALB der Kategorie — die Gruppen selbst bleiben
      // alphabetisch ("keine" zuletzt).
      for (const cat of Object.keys(g)) g[cat].sort(compareTasks);
    }
    const cats = Object.keys(g).sort((a, b) => {
      if (a === "keine") return 1;
      if (b === "keine") return -1;
      return a.localeCompare(b, "de");
    });
    return { grouped: g, sortedCats: cats };
  }, [filteredTasks, isSpecialView, compareTasks]);

  const allTasksSorted = useMemo(
    () => (isSpecialView ? [...filteredTasks].sort(compareTasks) : []),
    [filteredTasks, isSpecialView, compareTasks]
  );

  const areaInfo = !isSpecialView ? (baseAreas.find((k) => k.key === selected) || null) : null;

  const countByArea = useMemo(() => {
    const m = {};
    for (const t of tasks) m[t.area] = (m[t.area] || 0) + 1;
    return m;
  }, [tasks]);

  // count of appointments that will actually be visible in the "Alle Termine" view
  // (deduped Outlook entries, future only unless the user opts in via the status filter)
  // Local date — toISOString() is UTC and reports yesterday between 0–2 h CEST.
  const showPastAppointments = filters.status && filters.status.includes("completed");
  const visibleAppointmentsCount = useMemo(() => {
    const todayISO = formatISODate(new Date());
    const seen = new Set();
    let n = 0;
    for (const t of appointments) {
      if (t.outlook_id) {
        const key = `${t.date}|${t.text}|${t.start_time || ""}|${t.end_time || ""}`;
        if (seen.has(key)) continue;
        seen.add(key);
      }
      if (!showPastAppointments && t.date && t.date < todayISO) continue;
      n++;
    }
    return n;
  }, [appointments, showPastAppointments]);

  const isDefaultStatus = filters.status && filters.status.length === 1 && filters.status[0] === "open";
  const hasActiveFilters = (filters.status && filters.status.length > 0 && !isDefaultStatus) || (filters.person && filters.person.length > 0) || (filters.prio && filters.prio.length > 0) || (filters.area && filters.area.length > 0);

  // Multi-select handlers
  const toggleTaskSelection = (taskId, e) => {
    e.stopPropagation();
    setSelectedTasks((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  };

  const toggleSelectAll = (taskList) => {
    if (taskList.every((t) => selectedTasks.has(t.id))) {
      setSelectedTasks(new Set());
    } else {
      setSelectedTasks(new Set(taskList.map((t) => t.id)));
    }
  };

  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);

  const handleBulkDelete = () => {
    setConfirmBulkDelete(true);
  };

  // Bulk actions run their requests in PARALLEL — the old one-await-per-item
  // loop took N round-trips (noticeable on mobile networks). allSettled, not
  // all: a single failed request must not leave the menu/selection hanging
  // with the batch half-applied and zero feedback.
  const confirmBulkDeleteAction = async () => {
    await Promise.allSettled([...selectedTasks].map((id) => onDelete(id)));
    setSelectedTasks(new Set());
    setConfirmBulkDelete(false);
  };

  const handleBulkDuplicate = async () => {
    const tasksTodup = tasks.filter((t) => selectedTasks.has(t.id));
    await Promise.allSettled(tasksTodup.map((task) => onDuplicate(task)));
    setSelectedTasks(new Set());
  };

  const handleBulkMove = async (targetArea) => {
    await Promise.allSettled([...selectedTasks].map((id) => onQuickUpdate(id, { area: targetArea })));
    setSelectedTasks(new Set());
    setShowBulkMoveMenu(false);
  };

  const handleBulkCategory = async (cat) => {
    await Promise.allSettled([...selectedTasks].map((id) => onQuickUpdate(id, { category: cat || null })));
    setSelectedTasks(new Set());
    setShowBulkCatMenu(false);
    setBulkCatInput("");
  };

  // Area / Special View editing
  const openAreaEdit = () => {
    if (isSpecialView) {
      const sv = getSpecialView(selected);
      setEditLabel(sv.label);
      setEditColor(sv.color);
      setShowColorPicker(false);
      setAreaError("");
      setAreaEditing(true);
      return;
    }
    if (!areaInfo) return;
    // Editing/deleting real areas is admin-only — the server rejects it anyway,
    // so don't offer non-admins a modal whose save can only fail.
    if (!isAdmin) return;
    setEditLabel(areaInfo.label);
    setEditColor(areaInfo.border || "#3b82f6");
    setEditAdminOnly(areaInfo.admin_only || false);
    setShowColorPicker(false);
    setAreaError("");
    setAreaEditing(true);
  };

  const saveAreaEdit = async () => {
    if (isSpecialView) {
      const newConfig = { ...specialViewConfig, [selected]: { label: editLabel, color: editColor } };
      setSpecialViewConfig(newConfig);
      localStorage.setItem("specialViewConfig", JSON.stringify(newConfig));
      setAreaEditing(false);
      return;
    }
    try {
      const current = apiAreas.find((k) => k.key === selected);
      await updateArea(selected, {
        label: editLabel,
        border_color: editColor,
        bg: editColor + "20",
        text_color: editColor,
        admin_only: editAdminOnly,
        version: current?.version,
      });
      reloadAreas();
      setAreaEditing(false);
    } catch (e) {
      console.error("Bereich speichern fehlgeschlagen:", e);
      setAreaError("Bereich konnte nicht gespeichert werden: " + (e.message || "Unbekannter Fehler"));
    }
  };

  const handleDeleteArea = () => {
    if (!selected || isSpecialView) return;
    setConfirmDeleteArea(true);
  };

  const confirmDeleteAreaAction = async () => {
    try {
      await deleteArea(selected);
      setAreaEditing(false);
      // "__open__" is reachable from the sidebar — the old "__all__" view was
      // offered nowhere and stranded the user on an unlisted screen.
      setSelected("__open__");
      reloadAreas();
    } catch (e) {
      // Surface the failure — swallowing it made a rejected delete (e.g. missing
      // permission) look like nothing happened at all.
      setAreaError(e?.message || "Löschen fehlgeschlagen.");
    }
    setConfirmDeleteArea(false);
  };

  // New area
  const openAreaCreate = () => {
    setEditLabel("");
    setEditColor("#3b82f6");
    setEditAdminOnly(false);
    setShowColorPicker(false);
    setAreaError("");
    setAreaCreating(true);
  };

  const saveNewArea = async () => {
    if (!editLabel.trim()) { setAreaError("Bitte einen Namen angeben."); return; }
    const key = editLabel.trim().toLowerCase().replace(/[^a-z0-9]/g, "_");
    const abbreviation = editLabel.trim().substring(0, 4).toUpperCase();
    setAreaError("");
    try {
      await createArea({
        key,
        label: editLabel.trim(),
        abbreviation,
        bg: editColor + "20",
        border_color: editColor,
        text_color: editColor,
        admin_only: editAdminOnly,
      });
      reloadAreas();
      // Add to order
      const newOrder = [...areaOrder, key];
      setAreaOrder(newOrder);
      saveSetting("areaOrder", JSON.stringify(newOrder)).catch(() => {});
      setAreaCreating(false);
    } catch (e) {
      console.error("Bereich anlegen fehlgeschlagen:", e);
      const msg = String(e.message || "Unbekannter Fehler");
      if (/bereits|exist/i.test(msg)) {
        setAreaError(`Ein Bereich mit dem Namen „${editLabel.trim()}" existiert bereits.`);
      } else {
        setAreaError(msg);
      }
    }
  };

  const handleAreaDragStart = (e, key) => {
    dragArea.current = key;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", key);
  };

  const handleAreaDrop = (e, targetKey) => {
    e.preventDefault();
    if (dragArea.current === "__divider__") return;
    if (!dragArea.current || dragArea.current === targetKey) return;
    // Build full order including any areas not yet in areaOrder
    const fullOrder = [...allAreas.map((k) => k.key)];
    const fromIdx = fullOrder.indexOf(dragArea.current);
    const toIdx = fullOrder.indexOf(targetKey);
    if (fromIdx === -1 || toIdx === -1) return;
    fullOrder.splice(fromIdx, 1);
    fullOrder.splice(toIdx, 0, dragArea.current);
    setAreaOrder(fullOrder);
    saveSetting("areaOrder", JSON.stringify(fullOrder)).catch(() => {});
    dragArea.current = null;
  };

  const handleDividerDragStart = (e) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", "__divider__");
    dragArea.current = "__divider__";
  };

  const handleDividerDrop = (e, targetIndex) => {
    e.preventDefault();
    if (dragArea.current === "__divider__") {
      setDividerPos(targetIndex);
      localStorage.setItem("areaDividerPos", String(targetIndex));
      dragArea.current = null;
    }
  };

  const renderTable = (taskList, showCategory, forceHideArea = false) => {
    const showArea = !showCategory && !forceHideArea;
    const colCount = showArea ? 7 : 6;
    return (
      <table className={`list-table${showArea ? " list-table-with-area" : ""}`}>
        <thead>
          <tr>
            <th className="col-check">
              <label className="custom-checkbox" onClick={(e) => { e.stopPropagation(); toggleSelectAll(showCategory ? filteredTasks : taskList); }}>
                <span className={`checkbox-box ${taskList.length > 0 && taskList.every((t) => selectedTasks.has(t.id)) ? "checked" : taskList.some((t) => selectedTasks.has(t.id)) ? "partial" : ""}`}>
                  {taskList.length > 0 && taskList.every((t) => selectedTasks.has(t.id)) && (
                    <svg width="10" height="10" viewBox="0 0 12 12"><path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2" fill="none"/></svg>
                  )}
                  {taskList.some((t) => selectedTasks.has(t.id)) && !taskList.every((t) => selectedTasks.has(t.id)) && (
                    <svg width="10" height="10" viewBox="0 0 12 12"><line x1="2" y1="6" x2="10" y2="6" stroke="white" strokeWidth="2"/></svg>
                  )}
                </span>
              </label>
            </th>
            <SortHeader label="Name" sortKey="name" sort={sort} onSort={toggleSort} />
            <SortHeader label="Prio" sortKey="prio" sort={sort} onSort={toggleSort} className="col-prio" />
            {showArea && <SortHeader label="Bereich" sortKey="area" sort={sort} onSort={toggleSort} className="col-area" />}
            <SortHeader label="Person" sortKey="person" sort={sort} onSort={toggleSort} className="col-person" />
            <SortHeader label="Deadline" sortKey="deadline" sort={sort} onSort={toggleSort} className="col-deadline" />
            <SortHeader label="Kalender" sortKey="calendar" sort={sort} onSort={toggleSort} className="col-calendar" />
          </tr>
        </thead>
        <tbody>
          {showCategory ? (
            sortedCats.map((cat) => {
              const isCollapsed = !!collapsedCats[cat];
              const catTasks = grouped[cat];
              const allCatSelected = catTasks.length > 0 && catTasks.every((t) => selectedTasks.has(t.id));
              const someCatSelected = catTasks.some((t) => selectedTasks.has(t.id));
              return (
                <React.Fragment key={cat}>
                  <tr className="list-category-row" onClick={() => toggleCat(cat)}>
                    <td colSpan={colCount}>
                      <div className="list-category-header list-category-toggle">
                        <label className="custom-checkbox list-category-check" onClick={(e) => {
                          e.stopPropagation();
                          setSelectedTasks((prev) => {
                            const next = new Set(prev);
                            if (allCatSelected) {
                              catTasks.forEach((t) => next.delete(t.id));
                            } else {
                              catTasks.forEach((t) => next.add(t.id));
                            }
                            return next;
                          });
                        }}>
                          <span className={`checkbox-box ${allCatSelected ? "checked" : someCatSelected ? "partial" : ""}`}>
                            {allCatSelected && (
                              <svg width="10" height="10" viewBox="0 0 12 12"><path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2" fill="none"/></svg>
                            )}
                            {someCatSelected && !allCatSelected && (
                              <svg width="10" height="10" viewBox="0 0 12 12"><line x1="2" y1="6" x2="10" y2="6" stroke="white" strokeWidth="2"/></svg>
                            )}
                          </span>
                        </label>
                        <span className={`list-category-chevron ${isCollapsed ? "collapsed" : ""}`}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg></span>
                        {cat === "keine" ? "Keine Kategorie" : cat}
                        <span className="list-category-count">{catTasks.length}</span>
                        <button
                          className="list-category-add"
                          onClick={(e) => { e.stopPropagation(); onNewTask({ area: selected, category: cat === "keine" ? "" : cat }); }}
                          title="Neuer Task"
                        >+</button>
                      </div>
                    </td>
                  </tr>
                  {!isCollapsed && grouped[cat].map((task) => renderRow(task, false))}
                </React.Fragment>
              );
            })
          ) : (
            taskList.map((task) => renderRow(task, showArea))
          )}
        </tbody>
      </table>
    );
  };

  const toggleAppointmentSelection = (appointmentId, e) => {
    e.stopPropagation();
    setSelectedAppointments((prev) => {
      const next = new Set(prev);
      if (next.has(appointmentId)) {
        next.delete(appointmentId);
      } else {
        next.add(appointmentId);
      }
      return next;
    });
  };

  const toggleSelectAllAppointments = (list) => {
    // Outlook rows render without a checkbox — bulk select must never capture
    // them, otherwise Bulk-Löschen hits synced entries.
    const selectable = list.filter((t) => !t.outlook_id);
    if (selectable.length > 0 && selectable.every((t) => selectedAppointments.has(t.id))) {
      setSelectedAppointments(new Set());
    } else {
      setSelectedAppointments(new Set(selectable.map((t) => t.id)));
    }
  };

  const handleBulkDeleteAppointments = () => {
    setConfirmBulkDeleteAppointments(true);
  };

  // allSettled: one failed request (e.g. already deleted elsewhere) must not
  // reject the whole batch and leave the dialog/selection hanging.
  const confirmBulkDeleteAppointmentsAction = async () => {
    await Promise.allSettled([...selectedAppointments].map((id) => onDeleteAppointment(id)));
    setSelectedAppointments(new Set());
    setConfirmBulkDeleteAppointments(false);
  };

  const handleBulkDuplicateAppointments = async () => {
    const toDup = appointments.filter((t) => selectedAppointments.has(t.id));
    await Promise.allSettled(toDup.map((t) => onDuplicateAppointment(t)));
    setSelectedAppointments(new Set());
  };

  const renderAppointmentsTable = () => {
    // Shared pattern — the old local copy lacked the VM/NM variants, so
    // "AB Office VM" landed under "Allgemein" instead of "Tags".
    const templatePattern = APPT_TEMPLATE_PATTERN;
    const today = formatISODate(new Date()); // local, not UTC
    const showPast = filters.status && filters.status.includes("completed");

    // Deduplicate outlook appointments (same text + date + time = one entry)
    const deduped = [];
    const outlookSeen = {};
    for (const t of appointments) {
      if (t.outlook_id) {
        const key = `${t.date}|${t.text}|${t.start_time || ""}|${t.end_time || ""}`;
        if (outlookSeen[key]) {
          if (t.user_id && !outlookSeen[key].user_ids.includes(t.user_id)) {
            outlookSeen[key].user_ids.push(t.user_id);
          }
          continue;
        }
        const merged = { ...t, user_ids: t.user_id ? [t.user_id] : [] };
        outlookSeen[key] = merged;
        deduped.push(merged);
      } else {
        deduped.push(t);
      }
    }

    const visibleAppointments = deduped
      .filter((t) => showPast || !t.date || t.date >= today)
      // Time as tiebreak — same-day rows otherwise sit in API order (14:00
      // could precede 09:00); the calendar sorts by start_time too.
      .sort((a, b) => (a.date || "").localeCompare(b.date || "") || (a.start_time || "").localeCompare(b.start_time || ""));

    const tags = visibleAppointments.filter((t) => templatePattern.test(t.text));
    const normal = visibleAppointments.filter((t) => !templatePattern.test(t.text));
    // Checkbox state must mirror what is actually selectable (no Outlook rows) —
    // otherwise the header/group boxes could never reach the "checked" state.
    const selectableVisible = visibleAppointments.filter((t) => !t.outlook_id);
    const selectableNormal = normal.filter((t) => !t.outlook_id);
    const selectableTags = tags.filter((t) => !t.outlook_id);

    const renderRows = (list) =>
      list.map((appointment) => (
        <tr key={appointment.id} className={`list-row${appointment.date && appointment.date < today ? " list-row-done" : ""}${selectedAppointments.has(appointment.id) ? " list-row-selected" : ""}${appointment.outlook_id ? " list-row-outlook" : ""}`} onClick={() => !appointment.outlook_id && onEditAppointment && onEditAppointment(appointment)} style={appointment.outlook_id ? { cursor: "default" } : undefined}>
          <td className="col-check">
            {!appointment.outlook_id && (
            <label className="custom-checkbox" onClick={(e) => toggleAppointmentSelection(appointment.id, e)}>
              <span className={`checkbox-box ${selectedAppointments.has(appointment.id) ? "checked" : ""}`}>
                {selectedAppointments.has(appointment.id) && (
                  <svg width="10" height="10" viewBox="0 0 12 12"><path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2" fill="none"/></svg>
                )}
              </span>
            </label>
            )}
          </td>
          <td className="col-date">{formatDateShort(appointment.date)}</td>
          <td className="col-time">
            {appointment.start_time ? `${appointment.start_time}${appointment.end_time ? ` – ${appointment.end_time}` : ""}` : "—"}
          </td>
          <td className="col-name">{appointment.text}</td>
        </tr>
      ));

    return (
      <table className="list-table">
        <thead>
          <tr>
            <th className="col-check">
              <label className="custom-checkbox" onClick={(e) => { e.stopPropagation(); toggleSelectAllAppointments(visibleAppointments); }}>
                <span className={`checkbox-box ${selectableVisible.length > 0 && selectableVisible.every((t) => selectedAppointments.has(t.id)) ? "checked" : selectableVisible.some((t) => selectedAppointments.has(t.id)) ? "partial" : ""}`}>
                  {selectableVisible.length > 0 && selectableVisible.every((t) => selectedAppointments.has(t.id)) && (
                    <svg width="10" height="10" viewBox="0 0 12 12"><path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2" fill="none"/></svg>
                  )}
                  {selectableVisible.some((t) => selectedAppointments.has(t.id)) && !selectableVisible.every((t) => selectedAppointments.has(t.id)) && (
                    <svg width="10" height="10" viewBox="0 0 12 12"><line x1="2" y1="6" x2="10" y2="6" stroke="white" strokeWidth="2"/></svg>
                  )}
                </span>
              </label>
            </th>
            <th className="col-date">Datum</th>
            <th className="col-time">Uhrzeit</th>
            <th>Text</th>
          </tr>
        </thead>
        <tbody>
          {normal.length > 0 && (
            <>
              <tr className="list-category-row" onClick={() => setGeneralCollapsed(!generalCollapsed)}>
                <td colSpan={4}>
                  <div className="list-category-header list-category-toggle">
                    <label className="custom-checkbox list-category-check" onClick={(e) => {
                      e.stopPropagation();
                      const allNormalSel = selectableNormal.length > 0 && selectableNormal.every((t) => selectedAppointments.has(t.id));
                      setSelectedAppointments((prev) => {
                        const next = new Set(prev);
                        if (allNormalSel) {
                          selectableNormal.forEach((t) => next.delete(t.id));
                        } else {
                          selectableNormal.forEach((t) => next.add(t.id));
                        }
                        return next;
                      });
                    }}>
                      <span className={`checkbox-box ${selectableNormal.length > 0 && selectableNormal.every((t) => selectedAppointments.has(t.id)) ? "checked" : selectableNormal.some((t) => selectedAppointments.has(t.id)) ? "partial" : ""}`}>
                        {selectableNormal.length > 0 && selectableNormal.every((t) => selectedAppointments.has(t.id)) && (
                          <svg width="10" height="10" viewBox="0 0 12 12"><path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2" fill="none"/></svg>
                        )}
                        {selectableNormal.some((t) => selectedAppointments.has(t.id)) && !selectableNormal.every((t) => selectedAppointments.has(t.id)) && (
                          <svg width="10" height="10" viewBox="0 0 12 12"><line x1="2" y1="6" x2="10" y2="6" stroke="white" strokeWidth="2"/></svg>
                        )}
                      </span>
                    </label>
                    <span className={`list-category-chevron ${generalCollapsed ? "collapsed" : ""}`}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg></span>
                    Allgemein
                    <span className="list-category-count">{normal.length}</span>
                  </div>
                </td>
              </tr>
              {!generalCollapsed && renderRows(normal)}
            </>
          )}
          {tags.length > 0 && (
            <>
              <tr className="list-category-row" onClick={() => setTagsCollapsed(!tagsCollapsed)}>
                <td colSpan={4}>
                  <div className="list-category-header list-category-toggle">
                    <label className="custom-checkbox list-category-check" onClick={(e) => {
                      e.stopPropagation();
                      const allTagsSel = selectableTags.length > 0 && selectableTags.every((t) => selectedAppointments.has(t.id));
                      setSelectedAppointments((prev) => {
                        const next = new Set(prev);
                        if (allTagsSel) {
                          selectableTags.forEach((t) => next.delete(t.id));
                        } else {
                          selectableTags.forEach((t) => next.add(t.id));
                        }
                        return next;
                      });
                    }}>
                      <span className={`checkbox-box ${selectableTags.length > 0 && selectableTags.every((t) => selectedAppointments.has(t.id)) ? "checked" : selectableTags.some((t) => selectedAppointments.has(t.id)) ? "partial" : ""}`}>
                        {selectableTags.length > 0 && selectableTags.every((t) => selectedAppointments.has(t.id)) && (
                          <svg width="10" height="10" viewBox="0 0 12 12"><path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2" fill="none"/></svg>
                        )}
                        {selectableTags.some((t) => selectedAppointments.has(t.id)) && !selectableTags.every((t) => selectedAppointments.has(t.id)) && (
                          <svg width="10" height="10" viewBox="0 0 12 12"><line x1="2" y1="6" x2="10" y2="6" stroke="white" strokeWidth="2"/></svg>
                        )}
                      </span>
                    </label>
                    <span className={`list-category-chevron ${tagsCollapsed ? "collapsed" : ""}`}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg></span>
                    Tags
                    <span className="list-category-count">{tags.length}</span>
                  </div>
                </td>
              </tr>
              {!tagsCollapsed && renderRows(tags)}
            </>
          )}
          {visibleAppointments.length === 0 && (
            <tr><td colSpan={4} style={{ textAlign: "center", padding: "1rem", color: "#888" }}>Keine Termine</td></tr>
          )}
        </tbody>
      </table>
    );
  };

  const renderRow = (task, showArea) => {
    const areaObj = showArea ? allAreas.find((a) => a.key === task.area) : null;
    return (
      <tr
        key={task.id}
        className={`list-row ${task.completed ? "list-row-done" : ""} ${selectedTasks.has(task.id) ? "list-row-selected" : ""}`}
        onClick={() => onEdit(task)}
      >
        <td className="col-check">
          <label className="custom-checkbox" onClick={(e) => toggleTaskSelection(task.id, e)}>
            <span className={`checkbox-box ${selectedTasks.has(task.id) ? "checked" : ""}`}>
              {selectedTasks.has(task.id) && (
                <svg width="10" height="10" viewBox="0 0 12 12"><path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2" fill="none"/></svg>
              )}
            </span>
          </label>
        </td>
        <td className="col-name">
          <span className={task.completed ? "text-done" : ""}>{task.name}</span>
        </td>
        <td className="col-prio"><PrioIcon prio={task.prio} size={16} /></td>
        {showArea && (
          <td className="col-area">
            <span className="area-badge" style={{ borderColor: areaObj?.border || "#888", color: areaObj?.text || "#888" }}>
              {areaObj?.label || task.area}
            </span>
          </td>
        )}
        <td className="col-person"><span className="col-person-text">{(task.persons || []).map((p) => p.name).join(", ")}</span></td>
        <td className="col-deadline">{formatDateShort(task.deadline)}</td>
        <td className="col-calendar">{formatDateShort(task.calendar_date)}</td>
      </tr>
    );
  };

  if (!areasReady) {
    return (
      <div
        className="areas-page"
        style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh", color: "var(--text-muted)" }}
      >
        Laden…
      </div>
    );
  }

  return (
    <div className="areas-page">
      <div className="list-toolbar">
        <div className="filter-wrap" ref={filterRef}>
          <button
            className={`btn-filter-toggle ${hasActiveFilters ? "has-filters" : ""}`}
            onClick={() => setShowFilters(!showFilters)}
            title="Filter"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
            </svg>
            {hasActiveFilters && <span className="filter-dot" />}
          </button>
          {showFilters && (
            <>
              <div className="mobile-backdrop" onClick={() => setShowFilters(false)} />
              <div className="filter-overlay">
                <FilterBar filters={filters} onChange={onFiltersChange} employees={employees} areas={allAreas} showArea={false} />
              </div>
            </>
          )}
        </div>
      </div>

      {isMobile && mobileAreaOpen && (
        <>
          <div className="mobile-backdrop" onClick={() => setMobileAreaOpen(false)} />
          <div className="cat-mobile-dropdown-list">
            <button className={`cat-mobile-dropdown-item ${isOpenSelected ? "active" : ""}`} onClick={() => { setSelected("__open__"); setMobileAreaOpen(false); }}>
              {getSpecialView("__open__").label}<span className="cat-item-count">{openTasks.length}</span>
            </button>
            <button className={`cat-mobile-dropdown-item ${isAppointmentsSelected ? "active" : ""}`} onClick={() => { setSelected("__appointments__"); setMobileAreaOpen(false); }}>
              {getSpecialView("__appointments__").label}<span className="cat-item-count">{visibleAppointmentsCount}</span>
            </button>
            <div className="cat-mobile-dropdown-divider" />
            {allAreas.map((k) => (
              <button key={k.key} className={`cat-mobile-dropdown-item ${selected === k.key ? "active" : ""}`} onClick={() => { setSelected(k.key); setMobileAreaOpen(false); }}>
                {k.label}
                <span className="cat-item-count">{countByArea[k.key] || 0}</span>
              </button>
            ))}
          </div>
        </>
      )}

      <div className="cat-layout">
        {!isMobile && (
        <aside className="cat-sidebar">
          {/* Open Tasks */}
          <button
            className={`cat-item ${isOpenSelected ? "cat-item-active" : ""}`}
            style={isOpenSelected ? { background: getSpecialView("__open__").color + "20", borderColor: getSpecialView("__open__").color, color: getSpecialView("__open__").color } : {}}
            onClick={() => setSelected("__open__")}
          >
            <span className="cat-item-label">{getSpecialView("__open__").label}</span>
            <span
              className="cat-item-count"
              style={isOpenSelected ? { background: getSpecialView("__open__").color, color: "white" } : {}}
            >
              {openTasks.length}
            </span>
          </button>

          {/* All Appointments */}
          <button
            className={`cat-item ${isAppointmentsSelected ? "cat-item-active" : ""}`}
            style={isAppointmentsSelected ? { background: getSpecialView("__appointments__").color + "20", borderColor: getSpecialView("__appointments__").color, color: getSpecialView("__appointments__").color } : {}}
            onClick={() => setSelected("__appointments__")}
          >
            <span className="cat-item-label">{getSpecialView("__appointments__").label}</span>
            <span
              className="cat-item-count"
              style={isAppointmentsSelected ? { background: getSpecialView("__appointments__").color, color: "white" } : {}}
            >
              {visibleAppointmentsCount}
            </span>
          </button>

          <div className="cat-sidebar-divider" />

          {allAreas.map((k, i) => {
            const dk = k;
            return (
              <React.Fragment key={k.key}>
                {dividerPos === i && (
                  <div
                    className="cat-sidebar-divider-blue"
                    draggable
                    onDragStart={handleDividerDragStart}
                    onDragEnd={() => { dragArea.current = null; }}
                  />
                )}
                <div
                  className={`cat-item ${selected === k.key ? "cat-item-active" : ""}`}
                  style={
                    selected === k.key
                      ? { background: dk.border + "20", borderColor: dk.border, color: dk.border }
                      : {}
                  }
                  onClick={() => setSelected(k.key)}
                  draggable={true}
                  onDragStart={(e) => { e.stopPropagation(); handleAreaDragStart(e, k.key); }}
                  // Cleared on dragEnd — a stale ref otherwise let a later
                  // unrelated drag (e.g. selected text) reorder the sidebar.
                  onDragEnd={() => { dragArea.current = null; }}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onDrop={(e) => { e.stopPropagation();
                    if (dragArea.current === "__divider__") {
                      handleDividerDrop(e, i);
                    } else {
                      handleAreaDrop(e, k.key);
                    }
                  }}
                >
                  <span className="cat-item-label">
                    {dk.label}
                    {dk.admin_only && (
                      <svg className="cat-admin-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
                      </svg>
                    )}
                  </span>
                  <span
                    className="cat-item-count"
                    style={
                      selected === k.key
                        ? { background: dk.border, color: "white" }
                        : {}
                    }
                  >
                    {countByArea[k.key] || 0}
                  </span>
                </div>
              </React.Fragment>
            );
          })}
          {/* End-of-list drop zone: without it the divider could never be
              dragged back BELOW the last area (its default position). */}
          <div
            style={{ minHeight: 10 }}
            onDragOver={(e) => { if (dragArea.current === "__divider__") e.preventDefault(); }}
            onDrop={(e) => { e.stopPropagation(); handleDividerDrop(e, allAreas.length); }}
          >
            {dividerPos >= allAreas.length && (
              <div
                className="cat-sidebar-divider-blue"
                draggable
                onDragStart={handleDividerDragStart}
                onDragEnd={() => { dragArea.current = null; }}
              />
            )}
          </div>

          {isAdmin && (
            <>
              <div className="cat-sidebar-divider" />
              <button className="cat-item cat-add-area" onClick={openAreaCreate} title="Neuen Bereich hinzufügen">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                <span className="cat-item-label">Neuer Bereich</span>
              </button>
            </>
          )}
        </aside>
        )}

        <main className="cat-main">
          {isOpenSelected ? (
            <>
              <div className="cat-main-header" style={{ borderBottomColor: getSpecialView("__open__").color }} onClick={isMobile ? () => setMobileAreaOpen(true) : undefined}>
                <h2 style={{ color: getSpecialView("__open__").color }}>{getSpecialView("__open__").label}{isMobile && <svg className="cat-mobile-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>}</h2>
                <span className="cat-main-header-subtitle">Tasks ohne Kalendereintrag</span>
              </div>
              {openTasks.length === 0 ? (
                <div className="cat-empty"><p>Alle Tasks sind im Kalender eingeplant</p></div>
              ) : (
                renderTable(allTasksSorted, false, true)
              )}
            </>
          ) : isAppointmentsSelected ? (
            <>
              <div className="cat-main-header" style={{ borderBottomColor: getSpecialView("__appointments__").color }} onClick={isMobile ? () => setMobileAreaOpen(true) : undefined}>
                <h2 style={{ color: getSpecialView("__appointments__").color }}>
                  {getSpecialView("__appointments__").label}
                  {isMobile && <svg className="cat-mobile-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>}
                  <button
                    className="cat-header-add"
                    onClick={(e) => {
                      e.stopPropagation();
                      const today = new Date();
                      const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
                      onAddAppointment && onAddAppointment(iso);
                    }}
                    title="Neuer Termin"
                  >+</button>
                </h2>
              </div>
              {appointments.length === 0 ? (
                <div className="cat-empty"><p>Keine Termine vorhanden</p></div>
              ) : (
                renderAppointmentsTable()
              )}
            </>
          ) : (
            <>
              <div
                className="cat-main-header"
                style={{ borderBottomColor: areaInfo?.border }}
                onClick={isMobile ? () => setMobileAreaOpen(true) : undefined}
              >
                <h2 style={{ color: areaInfo?.border }}>
                  <span
                    className={isMobile || (!isAdmin && !isSpecialView) ? undefined : "cat-main-header-title"}
                    onClick={isMobile || (!isAdmin && !isSpecialView) ? undefined : (e) => { e.stopPropagation(); openAreaEdit(); }}
                    title={isMobile || (!isAdmin && !isSpecialView) ? undefined : "Bereich bearbeiten"}
                  >{areaInfo?.label}</span>
                  {isMobile && <svg className="cat-mobile-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>}
                  <button
                    className="cat-header-add"
                    onClick={() => onNewTask({ area: selected })}
                    title="Neuer Task"
                  >+</button>
                </h2>
              </div>


              {filteredTasks.length === 0 ? (
                <div className="cat-empty"><p>Keine Tasks für diesen Bereich</p></div>
              ) : (
                renderTable(filteredTasks, true)
              )}
            </>
          )}

          {selectedTasks.size > 0 && (
            <div className="bulk-action-bar">
              <span className="bulk-count">{selectedTasks.size} ausgewählt</span>
              <div className="bulk-actions">
                <div className="bulk-move-wrap" ref={bulkMoveMenuRef}>
                  <button className="bulk-btn bulk-btn-move" onClick={() => setShowBulkMoveMenu(!showBulkMoveMenu)}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14" /><path d="M12 5l7 7-7 7" />
                    </svg>
                    Verschieben
                  </button>
                  {showBulkMoveMenu && (
                    <div className="bulk-move-dropdown">
                      {allAreas.map((k) => (
                        <button
                          key={k.key}
                          className="bulk-move-option"
                          style={{ borderLeft: `3px solid ${k.border || k.text || "#888"}` }}
                          onClick={() => handleBulkMove(k.key)}
                        >
                          {k.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="bulk-move-wrap" ref={bulkCatMenuRef}>
                  <button className="bulk-btn bulk-btn-move" onClick={() => setShowBulkCatMenu(!showBulkCatMenu)}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 7h16" /><path d="M4 12h10" /><path d="M4 17h6" />
                    </svg>
                    Kategorie
                  </button>
                  {showBulkCatMenu && (
                    <div className="bulk-move-dropdown">
                      <div className="bulk-cat-input-wrap">
                        <input
                          className="bulk-cat-input"
                          value={bulkCatInput}
                          onChange={(e) => setBulkCatInput(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter" && bulkCatInput.trim()) handleBulkCategory(bulkCatInput.trim()); }}
                          placeholder="Neue Kategorie..."
                          autoFocus
                        />
                      </div>
                      {bulkCategories
                        .filter((k) => k.toLowerCase().includes(bulkCatInput.toLowerCase()))
                        .map((k) => (
                          <button key={k} className="bulk-move-option" onClick={() => handleBulkCategory(k)}>
                            {k}
                          </button>
                        ))}
                      <button className="bulk-move-option bulk-cat-none" onClick={() => handleBulkCategory("")}>
                        Keine Kategorie
                      </button>
                    </div>
                  )}
                </div>
                <button className="bulk-btn bulk-btn-duplicate" onClick={handleBulkDuplicate}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                  Duplizieren
                </button>
                <button className="bulk-btn bulk-btn-delete" onClick={handleBulkDelete}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                  Löschen
                </button>
              </div>
              <button className="bulk-btn-close" onClick={() => setSelectedTasks(new Set())}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          )}

          {selectedAppointments.size > 0 && (
            <div className="bulk-action-bar">
              <span className="bulk-count">{selectedAppointments.size} ausgewählt</span>
              <div className="bulk-actions">
                <button className="bulk-btn bulk-btn-duplicate" onClick={handleBulkDuplicateAppointments}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                  Duplizieren
                </button>
                <button className="bulk-btn bulk-btn-delete" onClick={handleBulkDeleteAppointments}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                  Löschen
                </button>
              </div>
              <button className="bulk-btn-close" onClick={() => setSelectedAppointments(new Set())}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          )}
        </main>
      </div>

      {areaEditing && (
        <div className="modal-overlay" onClick={() => setAreaEditing(false)}>
          <div className="modal-content area-popup" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{isSpecialView ? "Ansicht bearbeiten" : "Bereich bearbeiten"}</h2>
              <button className="modal-close-btn" onClick={() => setAreaEditing(false)} title="Schließen">&times;</button>
            </div>
            <div className="area-edit-row-pair">
              <div className="area-edit-cell">
                <label>Farbe</label>
                <button
                  ref={colorBtnRef}
                  type="button"
                  className="area-color-btn"
                  style={{ background: editColor }}
                  onClick={() => {
                    if (!showColorPicker && colorBtnRef.current) {
                      const rect = colorBtnRef.current.getBoundingClientRect();
                      setColorPickerPos({ top: rect.top, left: rect.right + 8 });
                    }
                    setShowColorPicker(!showColorPicker);
                  }}
                  title="Farbe ändern"
                />
                {showColorPicker && (
                  <div className="area-color-popover" ref={colorPickerRef} style={colorPickerPos ? { top: colorPickerPos.top, left: colorPickerPos.left } : {}}>
                    <HexColorPicker color={editColor} onChange={setEditColor} />
                    <input
                      className="area-color-hex-input"
                      value={editColor}
                      onChange={(e) => {
                        if (/^#[0-9a-fA-F]{0,6}$/.test(e.target.value)) setEditColor(e.target.value);
                      }}
                      maxLength={7}
                    />
                  </div>
                )}
              </div>
              <div className="area-edit-cell area-edit-cell-grow">
                <label>Name</label>
                <div className="area-edit-row-inline">
                  <input
                    type="text"
                    value={editLabel}
                    onChange={(e) => setEditLabel(e.target.value)}
                    className="area-edit-input"
                  />
                  {!isSpecialView && isAdmin && (
                    <button
                      type="button"
                      className={`area-lock-icon ${editAdminOnly ? "locked" : ""}`}
                      onClick={() => setEditAdminOnly(!editAdminOnly)}
                      title={editAdminOnly ? "Für alle sichtbar machen" : "Nur für Admins sichtbar machen"}
                    >
                      {editAdminOnly ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                        </svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 -2 24 26" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                          <path d="M7 11V4a5 5 0 0 1 10 0" />
                        </svg>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
            {areaError && <div className="form-error area-form-error">{areaError}</div>}
            <div className="area-edit-actions">
              {!isSpecialView && (
                <button className="btn btn-danger btn-sm" onClick={handleDeleteArea}>Löschen</button>
              )}
              <button className="btn btn-primary btn-sm" onClick={saveAreaEdit}>Speichern</button>
            </div>
          </div>
        </div>
      )}

      {areaCreating && (
        <div className="modal-overlay" onClick={() => setAreaCreating(false)}>
          <div className="modal-content area-popup" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Neuer Bereich</h2>
              <button className="modal-close-btn" onClick={() => setAreaCreating(false)} title="Schließen">&times;</button>
            </div>
            <div className="area-edit-row-pair">
              <div className="area-edit-cell">
                <label>Farbe</label>
                <button
                  ref={colorBtnRef}
                  type="button"
                  className="area-color-btn"
                  style={{ background: editColor }}
                  onClick={() => {
                    if (!showColorPicker && colorBtnRef.current) {
                      const rect = colorBtnRef.current.getBoundingClientRect();
                      setColorPickerPos({ top: rect.top, left: rect.right + 8 });
                    }
                    setShowColorPicker(!showColorPicker);
                  }}
                  title="Farbe ändern"
                />
                {showColorPicker && (
                  <div className="area-color-popover" ref={colorPickerRef} style={colorPickerPos ? { top: colorPickerPos.top, left: colorPickerPos.left } : {}}>
                    <HexColorPicker color={editColor} onChange={setEditColor} />
                    <input
                      className="area-color-hex-input"
                      value={editColor}
                      onChange={(e) => {
                        if (/^#[0-9a-fA-F]{0,6}$/.test(e.target.value)) setEditColor(e.target.value);
                      }}
                      maxLength={7}
                    />
                  </div>
                )}
              </div>
              <div className="area-edit-cell area-edit-cell-grow">
                <label>Name</label>
                <div className="area-edit-row-inline">
                  <input
                    type="text"
                    value={editLabel}
                    onChange={(e) => setEditLabel(e.target.value)}
                    className="area-edit-input"
                    autoFocus
                  />
                  <button
                    type="button"
                    className={`area-lock-icon ${editAdminOnly ? "locked" : ""}`}
                    onClick={() => setEditAdminOnly(!editAdminOnly)}
                    title={editAdminOnly ? "Für alle sichtbar machen" : "Nur für Admins sichtbar machen"}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      {editAdminOnly
                        ? <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                        : <path d="M7 11V7a5 5 0 0 1 5-5 5 5 0 0 1 5 5" />
                      }
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            {areaError && <div className="form-error area-form-error">{areaError}</div>}
            <div className="area-edit-actions">
              <button className="btn btn-primary btn-sm" style={{ marginLeft: "auto" }} onClick={saveNewArea}>Erstellen</button>
            </div>
          </div>
        </div>
      )}

      {confirmDeleteArea && (
        <ConfirmDialog
          message={(() => {
            const label = allAreas.find((k) => k.key === selected)?.label || selected;
            // The server deletes ALL the area's tasks — count the UNFILTERED
            // list, not the view (whose default filter hides completed tasks).
            const n = (allTasks ?? tasks).filter((t) => t.area === selected).length;
            return n > 0
              ? `Bereich "${label}" wirklich löschen? ${n} zugehörige Aufgabe${n === 1 ? " wird" : "n werden"} mitgelöscht.`
              : `Bereich "${label}" wirklich löschen?`;
          })()}
          onConfirm={confirmDeleteAreaAction}
          onCancel={() => setConfirmDeleteArea(false)}
        />
      )}

      {confirmBulkDelete && (
        <ConfirmDialog
          message={`${selectedTasks.size} Task${selectedTasks.size > 1 ? "s" : ""} wirklich löschen?`}
          onConfirm={confirmBulkDeleteAction}
          onCancel={() => setConfirmBulkDelete(false)}
        />
      )}

      {confirmBulkDeleteAppointments && (
        <ConfirmDialog
          message={`${selectedAppointments.size} Termin${selectedAppointments.size > 1 ? "e" : ""} wirklich löschen?`}
          onConfirm={confirmBulkDeleteAppointmentsAction}
          onCancel={() => setConfirmBulkDeleteAppointments(false)}
        />
      )}

    </div>
  );
}
